/* SSi website – Gift voucher page.
 *
 * A single configurator flow: pick a plan, write a note, pick a send
 * date + recipient, see the voucher update live, buy.
 *
 * Exports: GiftHero, GiftConfigurator, GiftHowItWorks, GiftFaq, GiftVoucherCard
 */
import { useState, useMemo } from 'react';

export function GiftHero({ t }) {
  return (
    <section className="ssi-gift__hero" style={gHeroStyles.wrap}>
      <div style={gHeroStyles.inner}>
        <div style={gHeroStyles.kicker}>{t.giftPageKicker}</div>
        <h1 style={gHeroStyles.h1}>{t.giftPageTitle}</h1>
        <p style={gHeroStyles.lede}>{t.giftPageLede}</p>
        <div style={gHeroStyles.bulletRow}>
          <div style={gHeroStyles.bullet}>
            <span style={gHeroStyles.bulletDot}>✓</span> {t.giftPageBullet1}
          </div>
          <div style={gHeroStyles.bullet}>
            <span style={gHeroStyles.bulletDot}>✓</span> {t.giftPageBullet2}
          </div>
          <div style={gHeroStyles.bullet}>
            <span style={gHeroStyles.bulletDot}>✓</span> {t.giftPageBullet3}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Configurator (left) + Voucher preview (right) ---------- */

const PLANS = [
  { id:'3mo',  label:'3 months',   months:3,  price:39,  per:'one-off', blurb:'A generous taster – enough to get through the method up to Level 1.' },
  { id:'6mo',  label:'6 months',   months:6,  price:75,  per:'one-off', blurb:'Half a year. Enough to reach holding-a-conversation.', featured:true },
  { id:'12mo', label:'12 months',  months:12, price:140, per:'one-off', blurb:'A full year – 12 months for the price of roughly 10.' },
];

const LANGUAGES = [
  { id:'welsh',      label:'Welsh',      sub:'Cymraeg' },
  { id:'spanish',    label:'Spanish',    sub:'Español' },
  { id:'french',     label:'French',     sub:'Français' },
  { id:'italian',    label:'Italian',    sub:'Italiano' },
  { id:'german',     label:'German',     sub:'Deutsch' },
  { id:'portuguese', label:'Portuguese', sub:'Português' },
  { id:'japanese',   label:'Japanese',   sub:'日本語' },
  { id:'korean',     label:'Korean',     sub:'한국어' },
  { id:'mandarin',   label:'Mandarin',   sub:'中文' },
  { id:'arabic',     label:'Arabic',     sub:'العربية' },
  { id:'english',    label:'English',    sub:'for non-natives' },
];

export function GiftConfigurator({ t }) {
  const [plan, setPlan]         = useState('6mo');
  const [language, setLanguage] = useState('welsh');
  const [recipient, setRecipient] = useState('');
  const [sender, setSender]       = useState('');
  const [message, setMessage]     = useState(t.giftDefaultMessage || '');
  const [delivery, setDelivery]   = useState('now');       // 'now' | 'date'
  const [date, setDate]           = useState('');
  const [printCopy, setPrintCopy] = useState(false);

  const selectedPlan = PLANS.find(p => p.id === plan);
  const selectedLang = LANGUAGES.find(l => l.id === language);
  const total = useMemo(() => selectedPlan.price + (printCopy ? 4.5 : 0), [selectedPlan, printCopy]);

  return (
    <section className="ssi-gift__config" style={configStyles.wrap}>
      <div className="ssi-gift__configGrid" style={configStyles.grid}>
        {/* --- LEFT: form --- */}
        <div style={configStyles.form}>
          <div style={configStyles.kicker}>{t.giftConfigKicker}</div>
          <h2 style={configStyles.h2}>{t.giftConfigTitle}</h2>

          {/* Step 1 – plan */}
          <fieldset style={configStyles.fieldset}>
            <legend style={configStyles.legend}><span style={configStyles.stepNum}>1</span> {t.giftStep1}</legend>
            <div className="ssi-gift__planRow" style={configStyles.planRow}>
              {PLANS.map(p => {
                const active = plan === p.id;
                return (
                  <button key={p.id} type="button" onClick={()=>setPlan(p.id)}
                    style={{
                      ...configStyles.planCard,
                      borderColor: active ? 'var(--brandRed)' : 'var(--border-strong)',
                      background: active ? 'rgba(194,19,12,.04)' : '#fff',
                      boxShadow: active ? '0 2px 0 rgba(194,19,12,.08), inset 0 0 0 1px var(--brandRed)' : 'none',
                    }}>
                    {p.featured && <span style={configStyles.planBadge}>{t.giftPopularLabel}</span>}
                    <div style={configStyles.planLabel}>{p.label}</div>
                    <div style={configStyles.planPrice}>£{p.price}</div>
                    <div style={configStyles.planBlurb}>{p.blurb}</div>
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* Step 2 – language */}
          <fieldset style={configStyles.fieldset}>
            <legend style={configStyles.legend}><span style={configStyles.stepNum}>2</span> {t.giftStep2}</legend>
            <div className="ssi-gift__langRow" style={configStyles.langRow}>
              {LANGUAGES.map(l => {
                const active = language === l.id;
                return (
                  <button key={l.id} type="button" onClick={()=>setLanguage(l.id)}
                    style={{
                      ...configStyles.langCard,
                      borderColor: active ? 'var(--brandRed)' : 'var(--border-strong)',
                      background: active ? 'rgba(194,19,12,.04)' : '#fff',
                    }}>
                    <div style={configStyles.langLabel}>{l.label}</div>
                    <div style={configStyles.langSub}>{l.sub}</div>
                  </button>
                );
              })}
              <div style={configStyles.langNote}>{t.giftLangNote}</div>
            </div>
          </fieldset>

          {/* Step 3 – recipient */}
          <fieldset style={configStyles.fieldset}>
            <legend style={configStyles.legend}><span style={configStyles.stepNum}>3</span> {t.giftStep3}</legend>
            <div className="ssi-gift__fieldRow" style={configStyles.fieldRow}>
              <label style={configStyles.field}>
                <span style={configStyles.fieldLabel}>{t.giftFieldRecipient}</span>
                <input
                  type="text"
                  value={recipient}
                  onChange={e=>setRecipient(e.target.value)}
                  placeholder={t.giftFieldRecipientPh}
                  style={configStyles.input}
                />
              </label>
              <label style={configStyles.field}>
                <span style={configStyles.fieldLabel}>{t.giftFieldSender}</span>
                <input
                  type="text"
                  value={sender}
                  onChange={e=>setSender(e.target.value)}
                  placeholder={t.giftFieldSenderPh}
                  style={configStyles.input}
                />
              </label>
            </div>
            <label style={{...configStyles.field, marginTop:14}}>
              <span style={configStyles.fieldLabel}>{t.giftFieldMessage}</span>
              <textarea
                value={message}
                onChange={e=>setMessage(e.target.value)}
                rows={4}
                placeholder={t.giftFieldMessagePh}
                style={{...configStyles.input, resize:'vertical', minHeight:90, lineHeight:1.5, fontFamily:'inherit'}}
              />
              <span style={configStyles.charCount}>{message.length} / 280</span>
            </label>
          </fieldset>

          {/* Step 4 – when to send */}
          <fieldset style={configStyles.fieldset}>
            <legend style={configStyles.legend}><span style={configStyles.stepNum}>4</span> {t.giftStep4}</legend>
            <div className="ssi-gift__deliveryRow" style={configStyles.deliveryRow}>
              <label style={{...configStyles.radioCard, borderColor: delivery==='now' ? 'var(--brandRed)' : 'var(--border-strong)'}}>
                <input type="radio" name="delivery" checked={delivery==='now'} onChange={()=>setDelivery('now')} style={{marginRight:10}}/>
                <div>
                  <div style={configStyles.radioLabel}>{t.giftDeliveryNow}</div>
                  <div style={configStyles.radioSub}>{t.giftDeliveryNowSub}</div>
                </div>
              </label>
              <label style={{...configStyles.radioCard, borderColor: delivery==='date' ? 'var(--brandRed)' : 'var(--border-strong)'}}>
                <input type="radio" name="delivery" checked={delivery==='date'} onChange={()=>setDelivery('date')} style={{marginRight:10}}/>
                <div style={{flex:1}}>
                  <div style={configStyles.radioLabel}>{t.giftDeliveryLater}</div>
                  <div style={configStyles.radioSub}>{t.giftDeliveryLaterSub}</div>
                  {delivery==='date' && (
                    <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{...configStyles.input, marginTop:10, maxWidth:220}}/>
                  )}
                </div>
              </label>
            </div>
            <label style={configStyles.checkboxRow}>
              <input type="checkbox" checked={printCopy} onChange={e=>setPrintCopy(e.target.checked)}/>
              <span>{t.giftPrintOption} <strong>+£4.50</strong></span>
            </label>
          </fieldset>

          {/* Total + CTA */}
          <div className="ssi-gift__totalBar" style={configStyles.totalBar}>
            <div>
              <div style={configStyles.totalLabel}>{t.giftTotalLabel}</div>
              <div style={configStyles.totalValue}>£{total.toFixed(2)}</div>
              <div style={configStyles.totalFine}>{t.giftTotalFine}</div>
            </div>
            <button type="button" className="btn btn--primary" style={{fontSize:15.5, padding:'14px 24px'}}>
              {t.giftCheckoutCta} →
            </button>
          </div>
        </div>

        {/* --- RIGHT: voucher preview (sticky) --- */}
        <div className="ssi-gift__previewCol" style={configStyles.previewCol}>
          <div style={configStyles.previewLabel}>{t.giftPreviewLabel}</div>
          <GiftVoucherCard
            months={selectedPlan.months}
            price={selectedPlan.price}
            language={selectedLang.label}
            recipient={recipient || t.giftDefaultRecipient}
            sender={sender || t.giftDefaultSender}
            message={message || t.giftDefaultMessage}
            code="SSi-GIFT-2026"
          />
          <div style={configStyles.previewNote}>{t.giftPreviewNote}</div>
        </div>
      </div>
    </section>
  );
}

export function GiftVoucherCard({ months, price, language, recipient, sender, message, code }) {
  return (
    <div style={voucherStyles.card}>
      {/* top band: gold stars + serial */}
      <div style={voucherStyles.top}>
        <svg width="100%" height="70" viewBox="0 0 480 70" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <pattern id="stars" x="0" y="0" width="70" height="70" patternUnits="userSpaceOnUse">
              <g fill="#FEC902">
                <path d="M20 15 L22 22 L29 24 L22 26 L20 33 L18 26 L11 24 L18 22 Z" opacity=".55"/>
                <path d="M55 40 L57 46 L63 48 L57 50 L55 56 L53 50 L47 48 L53 46 Z" opacity=".45"/>
                <circle cx="40" cy="20" r="1.3" opacity=".6"/>
                <circle cx="10" cy="55" r="1.3" opacity=".5"/>
              </g>
            </pattern>
          </defs>
          <rect width="480" height="70" fill="url(#stars)"/>
        </svg>
        <div style={voucherStyles.topLabel}>SaySomethingin · Gift voucher</div>
      </div>

      {/* body */}
      <div style={voucherStyles.body}>
        <div style={voucherStyles.toLine}>To <strong style={voucherStyles.toName}>{recipient}</strong></div>

        <div style={voucherStyles.dur}>
          <span style={voucherStyles.durNum}>{months}</span>
          <span style={voucherStyles.durUnit}>{months === 1 ? 'month' : 'months'}</span>
        </div>
        <div style={voucherStyles.durSub}>{language} with SaySomethingin</div>

        <div style={voucherStyles.message}>
          <span style={voucherStyles.messageMark}>“</span>
          {message}
          <span style={voucherStyles.messageMarkEnd}>”</span>
        </div>

        <div style={voucherStyles.foot}>
          <div style={voucherStyles.fromLine}>From <strong>{sender}</strong></div>
          <div style={voucherStyles.codeRow}>
            <span style={voucherStyles.codeLabel}>Redeem code</span>
            <span style={voucherStyles.codeValue}>{code}</span>
          </div>
        </div>

        <div style={voucherStyles.val}>Value £{price}</div>
      </div>
    </div>
  );
}

/* ---------- How it works ---------- */

export function GiftHowItWorks({ t }) {
  const steps = [
    { n:'01', h:t.giftHowStep1Title, b:t.giftHowStep1Body },
    { n:'02', h:t.giftHowStep2Title, b:t.giftHowStep2Body },
    { n:'03', h:t.giftHowStep3Title, b:t.giftHowStep3Body },
  ];
  return (
    <section className="ssi-gift__how" style={howStyles.wrap}>
      <div style={howStyles.head}>
        <div style={howStyles.kicker}>{t.giftHowKicker}</div>
        <h2 style={howStyles.h2}>{t.giftHowTitle}</h2>
      </div>
      <div className="ssi-gift__howGrid" style={howStyles.grid}>
        {steps.map(s => (
          <div key={s.n} style={howStyles.step}>
            <div style={howStyles.stepNum}>{s.n}</div>
            <div style={howStyles.stepH}>{s.h}</div>
            <div style={howStyles.stepB}>{s.b}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */

export function GiftFaq({ t }) {
  const [open, setOpen] = useState(0);
  const qs = [
    { q:t.giftFaqQ1, a:t.giftFaqA1 },
    { q:t.giftFaqQ2, a:t.giftFaqA2 },
    { q:t.giftFaqQ3, a:t.giftFaqA3 },
    { q:t.giftFaqQ4, a:t.giftFaqA4 },
    { q:t.giftFaqQ5, a:t.giftFaqA5 },
  ];
  return (
    <section className="ssi-gift__faq" style={faqStyles.wrap}>
      <div style={faqStyles.head}>
        <div style={faqStyles.kicker}>{t.giftFaqKicker}</div>
        <h2 style={faqStyles.h2}>{t.giftFaqTitle}</h2>
      </div>
      <div style={faqStyles.list}>
        {qs.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={i} style={{...faqStyles.item, borderColor: isOpen ? 'var(--brandRed)' : 'var(--border)'}}>
              <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} style={faqStyles.q}>
                <span style={{flex:1, textAlign:'left'}}>{item.q}</span>
                <span style={{...faqStyles.chev, transform: isOpen ? 'rotate(45deg)' : 'none'}}>+</span>
              </button>
              {isOpen && <div style={faqStyles.a}>{item.a}</div>}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- Styles ---------- */

const gHeroStyles = {
  wrap: { background:'var(--offWhite, #FDF5F5)', borderBottom:'1px solid var(--border)', padding:'80px 32px 72px' },
  inner: { maxWidth:960, margin:'0 auto', textAlign:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:18 },
  h1: { fontFamily:'var(--font-display)', fontWeight:700, fontSize:62, lineHeight:1.04, margin:'0 0 20px', letterSpacing:'-.02em', textWrap:'balance' },
  lede: { fontSize:19, lineHeight:1.55, color:'var(--grey)', margin:'0 auto 32px', maxWidth:'58ch', textWrap:'pretty' },
  bulletRow: { display:'inline-flex', flexWrap:'wrap', justifyContent:'center', gap:28, fontSize:14, color:'var(--fg-1)' },
  bullet: { display:'inline-flex', alignItems:'center', gap:8 },
  bulletDot: { display:'inline-flex', alignItems:'center', justifyContent:'center', width:20, height:20, borderRadius:'50%', background:'var(--brandRed)', color:'#fff', fontSize:11, fontWeight:700 },
};

const configStyles = {
  wrap: { padding:'80px 32px 96px', maxWidth:1400, margin:'0 auto' },
  grid: { display:'grid', gridTemplateColumns:'1.1fr 1fr', gap:72, alignItems:'start' },
  form: {},
  previewCol: { position:'sticky', top:24 },
  previewLabel: { fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--grey)', fontWeight:700, marginBottom:14 },
  previewNote: { fontSize:12.5, color:'var(--grey)', marginTop:16, lineHeight:1.5, textAlign:'center' },

  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:36, lineHeight:1.1, margin:'0 0 32px', letterSpacing:'-.01em' },

  fieldset: { border:'none', padding:0, margin:'0 0 36px' },
  legend: { fontFamily:'var(--font-display)', fontSize:19, fontWeight:700, color:'var(--fg-1)', marginBottom:14, display:'flex', alignItems:'center', gap:10 },
  stepNum: { display:'inline-flex', alignItems:'center', justifyContent:'center', width:26, height:26, borderRadius:'50%', background:'var(--brandRed)', color:'#fff', fontSize:13, fontWeight:600, fontFamily:'var(--font-body)' },

  planRow: { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 },
  planCard: { position:'relative', padding:'18px 16px', borderRadius:14, border:'1.5px solid', background:'#fff', cursor:'pointer', textAlign:'left', font:'inherit', transition:'all 120ms' },
  planBadge: { position:'absolute', top:-10, right:12, background:'var(--gold)', color:'#3a2900', fontSize:10.5, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', padding:'3px 10px', borderRadius:999 },
  planLabel: { fontSize:14, fontWeight:600, color:'var(--fg-1)' },
  planPrice: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.1, color:'var(--brandRed)', marginTop:6, letterSpacing:'-.01em' },
  planBlurb: { fontSize:12, color:'var(--grey)', marginTop:8, lineHeight:1.4 },

  langRow: { display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8, alignItems:'start' },
  langCard: { padding:'12px 14px', borderRadius:10, border:'1.5px solid', background:'#fff', cursor:'pointer', textAlign:'left', font:'inherit', transition:'all 120ms' },
  langLabel: { fontSize:13.5, fontWeight:600, color:'var(--fg-1)' },
  langSub: { fontSize:11.5, color:'var(--grey)', marginTop:2 },
  langNote: { gridColumn:'1 / -1', fontSize:12, color:'var(--grey)', marginTop:4, lineHeight:1.5 },

  fieldRow: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 },
  field: { display:'flex', flexDirection:'column', gap:6 },
  fieldLabel: { fontSize:12, fontWeight:500, color:'var(--grey)', letterSpacing:'.03em' },
  input: { width:'100%', padding:'10px 12px', border:'1.5px solid var(--border-strong)', borderRadius:8, fontSize:14, fontFamily:'inherit', color:'var(--fg-1)', background:'#fff', boxSizing:'border-box' },
  charCount: { fontSize:11, color:'var(--grey)', alignSelf:'flex-end', fontVariantNumeric:'tabular-nums' },

  deliveryRow: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 },
  radioCard: { display:'flex', alignItems:'flex-start', padding:'14px 16px', border:'1.5px solid', borderRadius:12, cursor:'pointer', background:'#fff' },
  radioLabel: { fontSize:14, fontWeight:600, color:'var(--fg-1)' },
  radioSub: { fontSize:12, color:'var(--grey)', marginTop:2, lineHeight:1.4 },
  checkboxRow: { display:'flex', alignItems:'center', gap:10, fontSize:13.5, color:'var(--fg-1)', padding:'10px 0' },

  totalBar: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'24px 28px', background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)', borderRadius:16, marginTop:8 },
  totalLabel: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--grey)', fontWeight:700, marginBottom:4 },
  totalValue: { fontFamily:'var(--font-display)', fontSize:32, lineHeight:1, color:'var(--fg-1)', letterSpacing:'-.01em' },
  totalFine: { fontSize:11.5, color:'var(--grey)', marginTop:6 },
};

/* Voucher card styles */
const voucherStyles = {
  card: {
    position:'relative',
    background:
      "radial-gradient(120% 90% at 20% 20%, #E8392B 0%, #C2130C 45%, #8E0A06 100%)",
    color:'#fff',
    borderRadius:20,
    overflow:'hidden',
    boxShadow:'0 24px 60px -20px rgba(73,3,0,.55), inset 0 0 0 1px rgba(255,255,255,.08)',
    aspectRatio:'3 / 4',
    display:'flex', flexDirection:'column',
  },
  top: { position:'relative', height:70, borderBottom:'1px dashed rgba(255,255,255,.35)', background:'rgba(0,0,0,.12)' },
  topLabel: { position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontSize:14, letterSpacing:'.2em', textTransform:'uppercase', color:'rgba(255,255,255,.85)' },
  body: { flex:1, padding:'36px 40px 32px', display:'flex', flexDirection:'column' },
  toLine: { fontSize:13, color:'rgba(255,255,255,.8)', letterSpacing:'.04em' },
  toName: { fontSize:18, color:'#fff', fontWeight:400 },
  dur: { display:'flex', alignItems:'baseline', gap:10, marginTop:18, marginBottom:2 },
  durNum: { fontFamily:'var(--font-display)', fontSize:80, lineHeight:1, letterSpacing:'-.02em' },
  durUnit: { fontFamily:'var(--font-display)', fontSize:28, color:'rgba(255,255,255,.9)' },
  durSub: { fontSize:14, color:'var(--gold)', letterSpacing:'.02em', marginBottom:24, textTransform:'none' },
  message: { fontFamily:'var(--font-display)', fontSize:18, lineHeight:1.45, color:'rgba(255,255,255,.95)', padding:'18px 0', borderTop:'1px dashed rgba(255,255,255,.25)', borderBottom:'1px dashed rgba(255,255,255,.25)', flex:1, display:'flex', alignItems:'center', textWrap:'pretty', fontStyle:'italic', minHeight:80 },
  messageMark: { color:'var(--gold)', marginRight:6, fontSize:24, alignSelf:'flex-start' },
  messageMarkEnd: { color:'var(--gold)', marginLeft:6, fontSize:24, alignSelf:'flex-end' },
  foot: { marginTop:24, display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:14 },
  fromLine: { fontSize:13, color:'rgba(255,255,255,.78)' },
  codeRow: { display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 },
  codeLabel: { fontSize:10, letterSpacing:'.16em', textTransform:'uppercase', color:'rgba(255,255,255,.6)' },
  codeValue: { fontFamily:'var(--font-display)', fontSize:14, letterSpacing:'.08em', color:'#fff', background:'rgba(0,0,0,.22)', padding:'5px 10px', borderRadius:6, fontVariantNumeric:'tabular-nums' },
  val: { position:'absolute', top:22, right:28, fontSize:11, letterSpacing:'.18em', textTransform:'uppercase', color:'rgba(255,255,255,.65)', zIndex:2 },
};

const howStyles = {
  wrap: { background:'#fff', padding:'48px 32px 96px', maxWidth:1240, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:640, margin:'0 auto 48px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.15, margin:0, letterSpacing:'-.01em' },
  grid: { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:28 },
  step: { background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)', borderRadius:18, padding:'32px 28px' },
  stepNum: { fontFamily:'var(--font-display)', fontSize:22, color:'var(--brandRed)', marginBottom:14, letterSpacing:'.06em' },
  stepH: { fontFamily:'var(--font-display)', fontSize:20, color:'var(--fg-1)', marginBottom:10, lineHeight:1.2 },
  stepB: { fontSize:14.5, color:'var(--grey)', lineHeight:1.55 },
};

const faqStyles = {
  wrap: { padding:'0 32px 120px', maxWidth:960, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:640, margin:'0 auto 40px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.15, margin:0, letterSpacing:'-.01em' },
  list: { display:'flex', flexDirection:'column', gap:10 },
  item: { border:'1px solid', borderRadius:14, background:'#fff', overflow:'hidden', transition:'border-color 120ms' },
  q: { width:'100%', padding:'18px 22px', background:'none', border:'none', display:'flex', alignItems:'center', gap:16, fontSize:16, fontFamily:'var(--font-display)', color:'var(--fg-1)', cursor:'pointer', fontWeight:600 },
  chev: { fontSize:22, color:'var(--brandRed)', fontWeight:300, transition:'transform 200ms', display:'inline-block' },
  a: { padding:'0 22px 22px', fontSize:15, color:'var(--grey)', lineHeight:1.6, textWrap:'pretty' },
};
